import React from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { BeforeAfterSlider } from '../components/BeforeAfterSlider';
import { ContactForm } from '../components/ContactForm';
import { TaskEstimator } from '../components/TaskEstimator';
import { COMPANY } from '../data/company';
import { getCdnImageUrl } from '../lib/utils';

export function HomePage() {

  return (
    <>
      <SEO 
        title="Altmuligmanden Juelsminde" 
        description="Din lokale altmuligmand i Juelsminde til reparationer, montering, maling, havearbejde, flytning, rydning og praktisk vedligeholdelse." 
      />

      {/* Hero Section */}
      <section className="relative bg-forest text-white overflow-hidden py-16 lg:py-24 border-b border-forest-light/40">
        <div className="absolute inset-0 bg-grid-pattern-dark opacity-30 pointer-events-none" />
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-copper/15 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Hero Text */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              
              <div className="inline-flex items-center gap-2.5 bg-forest-surface border border-white/20 text-white px-4 py-2 rounded-full text-xs sm:text-sm font-semibold tracking-wide shadow-md">
                <i className="fa-solid fa-location-dot text-white" aria-hidden="true" />
                <span>Din lokale altmuligmand i Juelsminde & omegn</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight font-display">
                Altmuligmanden <span className="text-white underline decoration-copper decoration-4 underline-offset-8">Juelsminde</span>
              </h1>

              <p className="text-xl sm:text-2xl font-semibold text-sand-light tracking-wide">
                Din lokale hjælp til små og store opgaver
              </p>

              <p className="text-sand/90 text-base sm:text-lg max-w-2xl leading-relaxed mx-auto lg:mx-0">
                Velkommen til din lokale altmuligmand – din pålidelige hjælp til både små og store opgaver i hjemmet. Jeg tilbyder professionel og effektiv service med fokus på kvalitet, grundighed og god dialog.
              </p>

              {/* Hero Action Buttons */}
              <div className="pt-2 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                <Link
                  to="/kontakt"
                  className="w-full sm:w-auto bg-copper hover:bg-copper-hover text-white font-bold px-8 py-4 rounded-xl shadow-xl transition-all duration-200 transform hover:-translate-y-0.5 flex items-center justify-center gap-3 cursor-pointer text-base ring-2 ring-copper/30"
                >
                  <i className="fa-solid fa-paper-plane" aria-hidden="true" />
                  <span>Få et uforpligtende tilbud</span>
                </Link>

                <a
                  href={`tel:${COMPANY.phone}`}
                  className="w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white border border-white/25 font-bold px-8 py-4 rounded-xl backdrop-blur-xs transition-all flex items-center justify-center gap-3 whitespace-nowrap text-base"
                >
                  <i className="fa-solid fa-phone text-white" aria-hidden="true" />
                  <span className="whitespace-nowrap">Ring: {COMPANY.phoneFormatted}</span>
                </a>
              </div>

              {/* Quick Trust Highlights Bar */}
              <div className="pt-6 border-t border-white/15 grid grid-cols-3 gap-3 text-center lg:text-left text-xs sm:text-sm text-sand/90 font-medium">
                <div className="flex flex-col lg:flex-row items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-white/20 text-white flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-check" aria-hidden="true" />
                  </div>
                  <span>Grundigt arbejde</span>
                </div>
                <div className="flex flex-col lg:flex-row items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-white/20 text-white flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-handshake" aria-hidden="true" />
                  </div>
                  <span>Aftaler overholdes</span>
                </div>
                <div className="flex flex-col lg:flex-row items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-white/20 text-white flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-coins" aria-hidden="true" />
                  </div>
                  <span>Fornuftige priser</span>
                </div>
              </div>

            </div>

            {/* Hero Image Framing */}
            <div className="lg:col-span-5 relative">
              <div className="craftsman-frame">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl border-2 border-white/20 bg-forest-dark group">
                  <img
                    src={getCdnImageUrl("https://assets.auxodev.dk/assets/3afae24a-c90c-4b91-94fe-484783460244/img-2498.jpeg", 1000)}
                    alt="Altmuligmanden Juelsminde - Jonas Vilhelmsen Varevogn"
                    className="w-full h-auto object-cover aspect-4/5 group-hover:scale-103 transition-transform duration-700"
                  />
                  
                  {/* Floating Craftsman Badge */}
                  <div className="absolute top-4 right-4 bg-forest/90 border border-white/20 text-white p-3 rounded-xl shadow-lg backdrop-blur-md flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-copper text-white flex items-center justify-center text-lg font-bold">
                      <i className="fa-solid fa-shield-halved" aria-hidden="true" />
                    </div>
                    <div className="text-left">
                      <p className="text-xs font-bold text-white">CVR: 41704209</p>
                      <p className="text-[11px] text-sand/80">Lokal & autoriseret</p>
                    </div>
                  </div>

                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-forest-dark via-forest/90 to-transparent p-6 text-white">
                    <p className="font-extrabold text-lg text-white font-display">Altmuligmanden Juelsminde</p>
                    <p className="text-xs text-sand/90">Klar med professionelt udstyr og bil på din adresse</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Interactive Task Estimator / Selector */}
      <TaskEstimator />

      {/* Unique Craftsman Pillars / Why Choose Altmuligmanden */}
      <section className="py-20 bg-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-3">
            <span className="text-copper font-bold text-xs uppercase tracking-widest bg-copper-light border border-copper-border px-4 py-1.5 rounded-full inline-block">
              Tryghed & Kvalitet
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-forest font-display">
              Hvorfor vælge Altmuligmanden Juelsminde?
            </h2>
            <p className="text-muted text-base sm:text-lg">
              Du får en nærværende og erfaren lokalmand, der tager hånd om dine opgaver med omhu.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            
            <div className="bg-white p-6 rounded-2xl border border-sand-border shadow-xs hover:shadow-lg transition-shadow space-y-3">
              <div className="w-12 h-12 rounded-xl bg-forest/10 text-forest flex items-center justify-center text-xl font-bold">
                <i className="fa-solid fa-user-check text-copper" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-forest font-display">Personlig Betjening</h3>
              <p className="text-muted text-xs sm:text-sm leading-relaxed">
                Du taler direkte med mig (Jonas), der udfører arbejdet. Ingen fordyrende mellemled eller forvirring.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-sand-border shadow-xs hover:shadow-lg transition-shadow space-y-3">
              <div className="w-12 h-12 rounded-xl bg-forest/10 text-forest flex items-center justify-center text-xl font-bold">
                <i className="fa-solid fa-handshake-simple text-copper" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-forest font-display">Tydelige Aftaler</h3>
              <p className="text-muted text-xs sm:text-sm leading-relaxed">
                Aftaler om tid og pris overholdes altid. Du kender prisen eller timeprisen på forhånd uden overraskelser.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-sand-border shadow-xs hover:shadow-lg transition-shadow space-y-3">
              <div className="w-12 h-12 rounded-xl bg-forest/10 text-forest flex items-center justify-center text-xl font-bold">
                <i className="fa-solid fa-house-flag text-copper" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-forest font-display">Lokal i Juelsminde</h3>
              <p className="text-muted text-xs sm:text-sm leading-relaxed">
                Kender nærområdet og kommer hurtigt ud til opgaver i Juelsminde, Hedensted, Barrit og omegn.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-sand-border shadow-xs hover:shadow-lg transition-shadow space-y-3">
              <div className="w-12 h-12 rounded-xl bg-forest/10 text-forest flex items-center justify-center text-xl font-bold">
                <i className="fa-solid fa-broom text-copper" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-forest font-display">Oprydning Inkluderet</h3>
              <p className="text-muted text-xs sm:text-sm leading-relaxed">
                Jeg rydder op efter mig selv og efterlader altid arbejdsstedet pænt og klar til brug.
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* Interactive Before & After Feature */}
      <BeforeAfterSlider />

      {/* About Altmuligmanden Teaser */}
      <section className="py-20 bg-sand-light relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="bg-white rounded-3xl p-8 sm:p-12 shadow-xl border border-sand-border grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            
            <div className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden shadow-xl border-2 border-sand-border">
                <img
                  src={getCdnImageUrl("https://assets.auxodev.dk/assets/3afae24a-c90c-4b91-94fe-484783460244/img-1851.jpeg", 800)}
                  alt="Om Altmuligmanden Juelsminde - Jonas Vilhelmsen"
                  className="w-full h-auto object-cover aspect-4/3"
                />
                <div className="absolute top-4 left-4 bg-copper text-white font-bold text-xs px-3.5 py-1.5 rounded-lg shadow-md uppercase tracking-wider">
                  Din lokale altmuligmand
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 space-y-5">
              <span className="text-copper font-bold text-xs uppercase tracking-widest bg-copper-light border border-copper-border px-4 py-1.5 rounded-full inline-block">
                Om Altmuligmanden Juelsminde
              </span>
              
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-forest font-display">
                Personlig betjening & håndværk med hjertet på det rette sted 🛠️
              </h2>

              <p className="text-muted text-base sm:text-lg leading-relaxed">
                Velkommen til din lokale altmuligmand – din pålidelige hjælp til både små og store opgaver i hjemmet. Jeg tilbyder professionel og effektiv service med fokus på kvalitet, grundighed og god dialog. Uanset om det gælder reparationer, montering, vedligeholdelse eller praktisk hjælp inde og ude, står jeg klar til at gøre hverdagen nemmere for dig.
              </p>

              <div className="pt-2 flex flex-wrap gap-4">
                <Link
                  to="/om-os"
                  className="inline-flex items-center gap-2.5 bg-forest hover:bg-forest-dark text-white font-bold px-7 py-3.5 rounded-xl shadow-md transition-colors cursor-pointer text-base"
                >
                  <span>Læs mere om mig</span>
                  <i className="fa-solid fa-user" aria-hidden="true" />
                </Link>

                <a
                  href={`tel:${COMPANY.phone}`}
                  className="inline-flex items-center gap-2.5 bg-sand hover:bg-sand-border text-forest font-bold px-7 py-3.5 rounded-xl transition-colors whitespace-nowrap text-base"
                >
                  <i className="fa-solid fa-phone text-copper" aria-hidden="true" />
                  <span className="whitespace-nowrap">Ring: {COMPANY.phoneFormatted}</span>
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Workflow Steps Section */}
      <section className="py-20 bg-forest text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern-dark opacity-30 pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-8 relative z-10">
          
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-3">
            <span className="text-white font-bold text-xs uppercase tracking-widest bg-white/10 px-4 py-1.5 rounded-full inline-block border border-white/20">
              Processen
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white font-display">
              Så nemt er det at få hjælp
            </h2>
            <p className="text-sand/80 text-base sm:text-lg">
              Fra første kontakt til veludført arbejde i dit hjem eller have.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            <div className="bg-forest-surface p-8 rounded-3xl border border-white/15 relative space-y-4">
              <span className="text-5xl font-black text-white/30 font-display block">01</span>
              <h3 className="text-xl font-bold text-white font-display">1. Kontakt & Beskrivelse</h3>
              <p className="text-sand/80 text-sm leading-relaxed">
                Ring på <a href={`tel:${COMPANY.phone}`} className="text-white underline whitespace-nowrap">{COMPANY.phoneFormatted}</a> eller udfyld formularen. Fortæl kort om opgavens omfang.
              </p>
            </div>

            <div className="bg-forest-surface p-8 rounded-3xl border border-white/15 relative space-y-4">
              <span className="text-5xl font-black text-white/30 font-display block">02</span>
              <h3 className="text-xl font-bold text-white font-display">2. Fast Tilbud / Aftale</h3>
              <p className="text-sand/80 text-sm leading-relaxed">
                Du modtager et uforpligtende tilbud med klar aftale om pris og tidspunkt for udførelsen.
              </p>
            </div>

            <div className="bg-forest-surface p-8 rounded-3xl border border-white/15 relative space-y-4 md:col-span-2 lg:col-span-1">
              <span className="text-5xl font-black text-white/30 font-display block">03</span>
              <h3 className="text-xl font-bold text-white font-display">3. Udførelse & Oprydning</h3>
              <p className="text-sand/80 text-sm leading-relaxed">
                Arbejdet udføres grundigt, og der ryddes pænt op efterfølgende, så alt står skarpt.
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-sand-light relative" id="kontakt-sektion">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Info Column */}
            <div className="lg:col-span-5 space-y-6">
              <span className="text-copper font-bold text-xs uppercase tracking-widest bg-copper-light border border-copper-border px-4 py-1.5 rounded-full inline-block">
                Kontakt
              </span>

              <h2 className="text-3xl sm:text-4xl font-extrabold text-forest font-display">
                Lad os tage en snak om din opgave
              </h2>

              <p className="text-muted text-base leading-relaxed">
                Kontakt mig i dag for en uforpligtende snak om din opgave – vi finder en løsning, der passer til dine behov og sørger for, at arbejdet bliver udført ordentligt og til tiden. 📞📧
              </p>

              <div className="space-y-4 pt-4">
                <div className="p-5 bg-white rounded-2xl border border-sand-border flex items-center gap-4 shadow-xs">
                  <div className="w-12 h-12 bg-forest text-white rounded-xl flex items-center justify-center text-xl shrink-0">
                    <i className="fa-solid fa-phone" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-xs text-muted font-bold uppercase tracking-wider block">Telefon</span>
                    <a href={`tel:${COMPANY.phone}`} className="text-forest font-bold hover:text-copper transition-colors text-base whitespace-nowrap">
                      {COMPANY.phoneFormatted}
                    </a>
                  </div>
                </div>

                <div className="p-5 bg-white rounded-2xl border border-sand-border flex items-center gap-4 shadow-xs">
                  <div className="w-12 h-12 bg-forest text-white rounded-xl flex items-center justify-center text-xl shrink-0">
                    <i className="fa-solid fa-envelope" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-xs text-muted font-bold uppercase tracking-wider block">E-mail</span>
                    <a href={`mailto:${COMPANY.email}`} className="text-forest font-bold hover:text-copper transition-colors text-base whitespace-nowrap">
                      {COMPANY.email}
                    </a>
                  </div>
                </div>

                <div className="p-5 bg-white rounded-2xl border border-sand-border flex items-center gap-4 shadow-xs">
                  <div className="w-12 h-12 bg-forest text-white rounded-xl flex items-center justify-center text-xl shrink-0">
                    <i className="fa-solid fa-location-dot" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-xs text-muted font-bold uppercase tracking-wider block">Adresse</span>
                    <span className="text-forest font-bold text-base block">{COMPANY.address}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Column */}
            <div className="lg:col-span-7">
              <ContactForm />
            </div>

          </div>
        </div>
      </section>
    </>
  );
}
